import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Eraser, Drum } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { audioClock } from "@/lib/audio";
import { SavedGrooves } from "@/components/SavedGrooves";

const KIT = "/media/samples/kit";
const ROWS = {
  Crash: { file: "crash_vl2_rr1.mp3", pitch: "A5", x: true },
  Ride: { file: "ride_vl2_rr1.mp3", pitch: "F5", x: true },
  "Hi-hat": { file: "hhc_vl3_rr1.mp3", pitch: "G5", x: true },
  "High tom": { file: "tomhi_vl3_rr1.mp3", pitch: "E5" },
  Snare: { file: "snare_vl4_rr1.mp3", pitch: "C5" },
  "Floor tom": { file: "tomlo_vl3_rr1.mp3", pitch: "A4" },
  Kick: { file: "kick_vl3_rr1.mp3", pitch: "F4" },
};
const ROCK = { "Hi-hat": [0, 1, 2, 3, 4, 5, 6, 7], Snare: [2, 6], Kick: [0, 4] };
// Levels grow with the course: more drums, then two bars.
export const GROOVE_LEVELS = {
  1: { title: "Groove Builder", rows: ["Hi-hat", "Snare", "Kick"], bars: 1, preset: ROCK },
  2: { title: "Groove Builder: add toms and crash", rows: ["Crash", "Hi-hat", "High tom", "Snare", "Floor tom", "Kick"], bars: 1, preset: { ...ROCK, Crash: [0], "Hi-hat": [1, 2, 3, 4, 5, 6, 7] } },
  3: { title: "Groove Builder: two-bar grooves with fills", rows: ["Crash", "Ride", "Hi-hat", "High tom", "Snare", "Floor tom", "Kick"], bars: 2, preset: { Ride: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], Snare: [2, 6, 10, 12], "High tom": [13], "Floor tom": [14, 15], Kick: [0, 3, 4, 8, 11, 12], Crash: [0] } },
};
const COUNT = ["1", "&", "2", "&", "3", "&", "4", "&"];
const bufs = {};
const hit = async (name) => {
  const ac = audioClock();
  const u = `${KIT}/${ROWS[name].file}`;
  bufs[u] = bufs[u] || await ac.decodeAudioData(await (await fetch(u)).arrayBuffer());
  const src = ac.createBufferSource(); src.buffer = bufs[u]; src.connect(ac.destination); src.start();
};
const fromPreset = (lv) => Object.fromEntries(lv.rows.map((r) => [r, new Set(lv.preset[r] || [])]));

const scoreNotes = (lv, grid, cur, bar) => {
  const out = [];
  for (let k = bar * 8; k < bar * 8 + 8; k += 1) {
    const on = lv.rows.filter((r) => grid[r].has(k));
    out.push(on.length
      ? { pitches: on.map((r) => ROWS[r].pitch), heads: on.map((r) => (ROWS[r].x ? "x" : null)), label: COUNT[k % 8], duration: "eighth", stemDir: "up", color: k === cur ? "#F59E0B" : undefined }
      : { rest: "quaver", label: COUNT[k % 8] });
  }
  return out;
};

export const GrooveBuilder = ({ level = 1, testId = "groove-builder" }) => {
  const lv = GROOVE_LEVELS[level];
  const steps = lv.bars * 8;
  const [grid, setGrid] = useState(() => fromPreset(lv));
  const [bpm, setBpm] = useState(level === 1 ? 80 : 96);
  const [cur, setCur] = useState(-1);
  const timer = useRef(null);
  const live = useRef({ grid, bpm });
  live.current = { grid, bpm };
  const stop = () => { clearTimeout(timer.current); timer.current = null; setCur(-1); };
  useEffect(() => stop, []);
  const play = () => {
    stop();
    let k = 0;
    const tick = () => {
      const g = live.current.grid;
      lv.rows.forEach((r) => g[r].has(k) && hit(r).catch(() => {}));
      setCur(k); k = (k + 1) % steps;
      timer.current = setTimeout(tick, 30000 / live.current.bpm);
    };
    tick();
  };
  const toggle = (r, k) => {
    setGrid((g) => { const s = new Set(g[r]); if (s.has(k)) s.delete(k); else { s.add(k); hit(r).catch(() => {}); } return { ...g, [r]: s }; });
  };
  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Drum size={14} /> {lv.title}</p>
      <p className="mt-2 text-sm text-muted-foreground">Tap the squares to build your own beat, then press Play to hear it loop. Watch it appear on the drum score underneath.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="border-separate border-spacing-1" data-testid={`${testId}-grid`}>
          <thead><tr><th className="sticky left-0 z-10 bg-card" />{Array.from({ length: steps }, (_, k) => <th key={k} className={`${lv.bars > 1 ? "w-6" : "w-8"} text-center font-mono text-[11px] ${k === cur ? "text-primary" : "text-muted-foreground"}`}>{COUNT[k % 8]}</th>)}</tr></thead>
          <tbody>
            {lv.rows.map((r) => (
              <tr key={r}>
                <td className="sticky left-0 z-10 whitespace-nowrap bg-card pr-2 text-xs font-semibold">{r}</td>
                {Array.from({ length: steps }, (_, k) => (
                  <td key={k} className={k % 8 === 7 && k < steps - 1 ? "pr-2" : ""}>
                    <button onClick={() => toggle(r, k)} aria-pressed={grid[r].has(k)} aria-label={`${r}, count ${COUNT[k % 8]}${lv.bars > 1 ? `, bar ${Math.floor(k / 8) + 1}` : ""}`}
                      data-testid={`${testId}-cell-${r.toLowerCase().replace(/\s/g, "-")}-${k}`}
                      className={`${lv.bars > 1 ? "h-6 w-6" : "h-8 w-8"} rounded-md border transition-colors ${grid[r].has(k) ? "border-primary bg-primary" : k === cur ? "border-primary/60 bg-primary/10" : "border-border bg-muted hover:border-primary"}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={timer.current ? stop : play} data-testid={`${testId}-play`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-105">
          {cur >= 0 ? <><Square size={12} /> Stop</> : <><Play size={13} /> Play</>}
        </button>
        <button onClick={() => setGrid(Object.fromEntries(lv.rows.map((r) => [r, new Set()])))} data-testid={`${testId}-clear`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs"><Eraser size={12} /> Clear</button>
        <button onClick={() => setGrid(fromPreset(lv))} data-testid={`${testId}-preset`} className="rounded-full border border-border px-4 py-2 text-xs">Example groove</button>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">Tempo
          <input type="range" min="60" max="140" value={bpm} onChange={(e) => setBpm(Number(e.target.value))} data-testid={`${testId}-tempo`} className="accent-[hsl(var(--primary))]" />
          <span className="w-14 font-mono text-foreground" data-testid={`${testId}-bpm`}>{bpm} BPM</span>
        </label>
      </div>
      <SavedGrooves level={level} grid={grid} bpm={bpm} testId={testId}
        onLoad={(g) => { setGrid(Object.fromEntries(lv.rows.map((r) => [r, new Set((g.grid[r] || []).filter((k) => k < steps))]))); setBpm(g.bpm); }} />
      <div className="mt-4" data-testid={`${testId}-score`}>
        {Array.from({ length: lv.bars }, (_, b) => (
          <div key={b}>
            {lv.bars > 1 && <p className="text-xs font-semibold text-muted-foreground">Bar {b + 1}</p>}
            <StaffViewer clef="percussion" width={480} playOnClick={false} time={b === 0 ? "4/4" : undefined} notes={scoreNotes(lv, grid, cur, b)} testId={lv.bars > 1 ? `${testId}-staff-${b + 1}` : `${testId}-staff`} />
          </div>
        ))}
      </div>
    </div>
  );
};
